<template>
  <div id="share-embed-panel" class="panel-container is-animated slide-from-right">
    <header class="panel-header">
      <div class="panel-left-item">
        <base-icon class="icon-button" name="arrow_back" @click="backToMainPanel" />
      </div>
      <h3>{{ __('Embed this padlet') }}</h3>
      <div class="panel-right-item" />
    </header>
    <div class="panel-body">
      <div v-if="isPasswordProtected" class="tv-cell tv-cell-subtitle warning-scheme">
        <base-icon class="caption-type" name="warning" />
        <div>
          <strong>{{ __('This padlet is password protected.') }}</strong>
          <span>{{
            __('You can still embed it but users may be directed to provide the password in a new tab.')
          }}</span>
        </div>
      </div>
      <div v-if="isPrivate" class="tv-cell tv-cell-subtitle warning-scheme">
        <base-icon class="caption-type" name="warning" />
        <div>
          <strong>{{ __('This padlet is private.') }}</strong>
          <span>{{ __('Invited contributors may be directed to log in and acccess it on a new tab.') }}</span>
        </div>
      </div>
      <div v-if="isOrgWide" class="tv-cell tv-cell-subtitle warning-scheme">
        <base-icon class="caption-type" name="warning" />
        <div>
          <strong>{{ __('This padlet is restricted to users in your organization.') }}</strong>
          <span>{{ __('You can still embed it but users may be directed to log in on a new tab.') }}</span>
        </div>
      </div>
      <div v-if="isLibraryWide" class="tv-cell tv-cell-subtitle warning-scheme">
        <base-icon class="caption-type" name="warning" />
        <div>
          <strong>
            {{
              isSchoolLibrary
                ? __('This padlet is restricted to users in your school.')
                : __('This padlet is restricted to users in your team.')
            }}
          </strong>
          <span>{{ __('You can still embed it but users may be directed to log in on a new tab.') }}</span>
        </div>
      </div>
      <div v-if="isLoggedInUsersOnly" class="tv-cell tv-cell-subtitle warning-scheme">
        <base-icon class="caption-type" name="warning" />
        <div>
          <strong> {{ __('This padlet is members-only.') }} </strong>
          <span>{{ __('You can still embed it but users may be directed to log in on a new tab.') }}</span>
        </div>
      </div>
      <section class="embed-code-section">
        <header class="section-header">
          <div>{{ __('Full embed') }}</div>
          <div v-if="!isMobileApp" class="section-header-more">
            <a href="https://padlet.help/fun-things-you-can-do/how-to-embed-padlet" target="_blank">
              {{ __('Help') }}
            </a>
          </div>
        </header>
        <div class="section-body">
          <div class="section-list tv">
            <div class="tv-cell small-caption-type">
              {{
                __(
                  'A fully functional padlet that fits the dimensions of your webpage or blog. Visitors can post, remake, and share from here.',
                )
              }}
            </div>
            <div class="tv-cell">
              {{ __('Copy and paste this code in the text editor of your blog or HTML of your website.') }}
            </div>
            <div class="tv-cell">
              <code data-testid="surfaceFullEmbedCode">{{ embedCode }}</code>
            </div>
          </div>
          <div v-if="canCopyText" class="section-action">
            <div class="button-list">
              <button class="text-button button" @click="copyEmbedCode">{{ __('Copy') }}</button>
            </div>
          </div>
        </div>
      </section>
      <section class="short-code-section">
        <header class="section-header header-with-block">
          <div>{{ __('Embedding on a wordpress.com site?') }}</div>
        </header>
        <div class="section-body">
          <div class="section-list tv">
            <div class="tv-cell small-caption-type">
              {{
                __(
                  'N.B. The following method only works on wordpress.com sites. For self-hosted wordpress.org sites, please use the code above.',
                )
              }}
            </div>
            <div class="tv-cell">
              {{ __('Just paste the padlet URL in the post and the padlet will be embedded automatically.') }}
            </div>
            <div class="tv-cell">
              <code>{{ wpShortCode }}</code>
            </div>
          </div>
          <div class="button-list section-action">
            <button v-if="canCopyText" class="text-button button" @click="copyWordpressShortCode">
              {{ __('Copy') }}
            </button>
          </div>
        </div>
      </section>
      <section class="preview-code-section">
        <header class="section-header header-with-block">
          <div>{{ __('Preview card embed') }}</div>
        </header>
        <div class="section-body">
          <div class="section-list tv">
            <div class="tv-cell small-caption-type">
              {{
                __(
                  'A preview image that shows the title, owner, and up to three images from the padlet. Clicking the image opens the padlet in a new browser window.',
                )
              }}
            </div>
            <div class="tv-cell">
              {{ __('Copy and paste this code in the text editor of your blog or HTML of your website.') }}
            </div>
            <div class="tv-cell">
              <code data-testid="surfacePreviewEmbedCode">{{ previewOnlyEmbedCode }}</code>
            </div>
          </div>
          <div class="button-list section-action">
            <button v-if="canCopyText" class="text-button button" @click="copyPreviewOnlyEmbedCode">
              {{ __('Copy') }}
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { copyToClipboard } from 'bits/clipboard'
import BaseIcon from 'vuecomponents/base_icon'
import { mapActions, mapGetters, mapState } from 'vuex'
import { ShareSubpanel } from 'vuexstore/modules/share'

export default {
  components: { BaseIcon },
  computed: {
    ...mapState(['wall', 'isMobile', 'isApp']),
    ...mapState({
      embedCodeUrlTemplate: (state) => state.constants.embedCodeUrlTemplate,
      previewEmbedCodeUrlTemplate: (state) => state.constants.previewEmbedCodeUrlTemplate,
    }),
    ...mapGetters([
      'isPasswordProtected',
      'isPrivate',
      'isLibraryWide',
      'isSchoolLibrary',
      'isOrgWide',
      'isLoggedInUsersOnly',
    ]),
    isMobileApp() {
      return this.isMobile && this.isApp
    },
    canCopyText() {
      return true
    },
    previewOnlyEmbedCode() {
      const previewEmbedUrl = this.wall.links.preview_embed
      return this.previewEmbedCodeUrlTemplate.replace('{previewEmbedUrl}', previewEmbedUrl)
    },
    embedCode() {
      const embedUrl = this.wall.links.embed
      return this.embedCodeUrlTemplate.replace('{embedUrl}', embedUrl)
    },
    wpShortCode() {
      return this.wall.links.show
    },
  },
  methods: {
    ...mapActions('share', ['changeActivePanel']),
    ...mapActions('snackbars', ['setSnackbar']),
    async copy(thing) {
      const result = await copyToClipboard(thing)
      if (result) {
        this.setSnackbar({ message: this.__('Copied to your clipboard.') })
      } else {
        this.setSnackbar({ message: this.__('Could not copy to your clipboard.') })
      }
    },
    copyEmbedCode() {
      this.copy(this.embedCode)
    },
    copyWordpressShortCode() {
      this.copy(this.wpShortCode)
    },
    copyPreviewOnlyEmbedCode() {
      this.copy(this.previewOnlyEmbedCode)
    },
    backToMainPanel() {
      this.changeActivePanel(ShareSubpanel.Main)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
code {
  display: block;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: $super-small-font-size;
  line-height: $super-small-line-height;
  font-weight: 600;
}

.panel-body {
  position: relative;
}
</style>
